import { useCallback, useEffect, useRef, useState } from "react";

const API = process.env.REACT_APP_BACKEND_URL;
const SENT_END = /([.!?।]+["')\]]?\s+|\n+)/;

/* Sentence-by-sentence TTS queue: speaks while the answer is still streaming for low latency. */
export const useSpeaker = (sessionId, lang, volume = 1) => {
  const [muted, setMuted] = useState(() => localStorage.getItem("pl_voice_muted") === "1");
  const [speaking, setSpeaking] = useState(false);
  const queue = useRef([]);
  const audio = useRef(null);
  const playing = useRef(false);
  const pending = useRef("");
  const gen = useRef(0);
  const mutedRef = useRef(muted);
  mutedRef.current = muted;
  const vol = useRef(volume);
  vol.current = volume;
  useEffect(() => { if (audio.current) audio.current.volume = Math.min(1, Math.max(0, volume ?? 1)); }, [volume]);

  const stop = useCallback(() => {
    gen.current += 1; queue.current = []; pending.current = "";
    if (audio.current) { audio.current.pause(); audio.current.src = ""; audio.current = null; }
    playing.current = false; setSpeaking(false);
  }, []);

  const pump = useCallback(async () => {
    if (playing.current) return;
    const item = queue.current.shift();
    if (!item) { setSpeaking(false); return; }
    playing.current = true; setSpeaking(true);
    const g = gen.current;
    try {
      const r = await fetch(`${API}/api/chat/voice/speak`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ session_id: sessionId, text: item, lang }) });
      if (!r.ok || g !== gen.current) throw new Error("tts");
      const url = URL.createObjectURL(await r.blob());
      if (g !== gen.current) { URL.revokeObjectURL(url); throw new Error("stale"); }
      await new Promise((res) => { const a = new Audio(url); a.volume = Math.min(1, Math.max(0, vol.current ?? 1)); audio.current = a; a.onended = a.onerror = () => { URL.revokeObjectURL(url); res(); }; a.play().catch(res); });
    } catch { /* skip this chunk */ }
    playing.current = false;
    if (g === gen.current) pump();
  }, [sessionId, lang]);

  const enqueue = useCallback((text) => { const t = (text || "").trim(); if (!t || mutedRef.current) return; queue.current.push(t); pump(); }, [pump]);

  /* Feed streamed text; complete sentences are spoken as soon as they close. */
  const feed = useCallback((delta) => {
    if (mutedRef.current) return;
    pending.current += delta;
    let m;
    while ((m = SENT_END.exec(pending.current))) {
      const cut = m.index + m[0].length;
      const sentence = pending.current.slice(0, cut);
      pending.current = pending.current.slice(cut);
      if (sentence.trim().length > 1) enqueue(sentence);
    }
  }, [enqueue]);
  const flush = useCallback(() => { const rest = pending.current; pending.current = ""; if (rest.trim()) enqueue(rest); }, [enqueue]);
  const toggleMute = useCallback(() => { setMuted((m) => { const n = !m; localStorage.setItem("pl_voice_muted", n ? "1" : "0"); if (n) stop(); return n; }); }, [stop]);

  useEffect(() => () => stop(), [stop]);
  return { muted, speaking, toggleMute, stop, speak: enqueue, feed, flush };
};

/* MediaRecorder-based push-to-talk with permission handling and Whisper transcription. */
export const useRecorder = ({ sessionId, lang, onTranscript, onError }) => {
  const [state, setState] = useState("idle"); // idle | recording | transcribing | denied | unsupported
  const rec = useRef(null);
  const chunks = useRef([]);
  const timer = useRef(null);
  const supported = typeof window !== "undefined" && !!(navigator.mediaDevices?.getUserMedia && window.MediaRecorder);

  const stop = useCallback(() => { if (rec.current && rec.current.state !== "inactive") rec.current.stop(); clearTimeout(timer.current); }, []);

  const start = useCallback(async () => {
    if (!supported) { setState("unsupported"); onError?.("Your browser doesn't support voice input — please type instead."); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"].find((m) => window.MediaRecorder.isTypeSupported?.(m)) || "";
      const mr = new window.MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunks.current = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunks.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setState("transcribing");
        try {
          const blob = new Blob(chunks.current, { type: mr.mimeType || "audio/webm" });
          const fd = new FormData(); fd.append("file", blob, `clip.${(mr.mimeType || "webm").includes("mp4") ? "mp4" : (mr.mimeType || "").includes("ogg") ? "ogg" : "webm"}`); fd.append("session_id", sessionId); fd.append("lang", lang || "auto");
          const r = await fetch(`${API}/api/chat/voice/transcribe`, { method: "POST", body: fd });
          const d = await r.json();
          if (!r.ok) throw new Error(d.detail || "Speech recognition failed");
          if (!d.text) { onError?.(d.reason === "empty" ? "I didn't hear anything — tap the mic and speak, or type below." : "I couldn't catch that — please try again or type your question."); }
          else onTranscript?.(d.text, d);
        } catch (ex) { onError?.(`${ex.message}. You can type instead.`); }
        setState("idle");
      };
      mr.start(250); rec.current = mr; setState("recording");
      timer.current = setTimeout(() => stop(), 30000);
    } catch (ex) {
      const denied = ex?.name === "NotAllowedError" || ex?.name === "SecurityError";
      setState(denied ? "denied" : "idle");
      onError?.(denied ? "Microphone access was blocked. Allow the microphone in your browser's site settings, or simply type your question." : "Could not start the microphone — please type instead.");
    }
  }, [supported, sessionId, lang, onTranscript, onError, stop]);

  return { state, start, stop, supported };
};
